/**
 * Reports whether Waterlily has read the advisor's whole book yet, and lets them ask again.
 *
 * Reading a book takes many background passes, so "when was this last complete" is a question the
 * advisor genuinely needs answered — otherwise stale data and a broken connection look identical.
 * The status query polls only while a run is active; there is nothing to watch the rest of the
 * time.
 *
 * Nothing here fails silently. A status that could not be fetched is reported as such, so the page
 * does not claim the book was never read. A refused read and a refused setting both come back with
 * the server's reason. A refusal for missing consent is cleared once consent is given, because giving
 * it is the answer to that message.
 */
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { prospectingApi, queryKeys } from '@client/api/integrations';
import type { Prospecting } from '@client/api/types';

const ACTIVE_POLL_MS = 10_000;

interface UseCrmBookSyncOptions {
  objectName?: string;
  /** Whether the advisor has agreed to the full contact read, as far as the page knows. */
  bookReadConsented?: boolean;
}

export function useCrmBookSync(
  provider: Prospecting.Provider,
  { objectName = 'Contact', bookReadConsented = false }: UseCrmBookSyncOptions = {},
) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.prospecting.sync(provider, objectName),
    queryFn: () => prospectingApi.sync.status(provider, objectName),
    refetchInterval: ({ state }) =>
      state.data?.runs.some(run => run.status === 'pending' || run.status === 'running')
        ? ACTIVE_POLL_MS
        : false,
  });

  const runs = query.data?.runs ?? [];
  const active = runs.find(run => run.status === 'pending' || run.status === 'running');
  const lastCompleted = runs.find(run => run.status === 'completed');

  const settingsQuery = useQuery({
    queryKey: queryKeys.prospecting.syncSettings(provider, objectName),
    queryFn: () => prospectingApi.sync.settings(provider, objectName),
  });

  const saveSettings = useMutation({
    mutationFn: (settings: { refreshIntervalHours: number; autoRefreshEnabled: boolean }) =>
      prospectingApi.sync.saveSettings(provider, { objectName, ...settings }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.prospecting.syncSettings(provider, objectName),
      }),
  });

  const mutation = useMutation({
    mutationFn: () => prospectingApi.sync.request(provider, objectName),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.prospecting.sync(provider, objectName),
      }),
  });

  const { reset: clearRequestError } = mutation;
  useEffect(() => {
    if (bookReadConsented) clearRequestError();
  }, [bookReadConsented, clearRequestError]);

  return {
    isLoading: query.isLoading,
    /** The status could not be fetched and there is no earlier answer to show. */
    statusUnavailable: query.isError && query.data === undefined,
    active,
    lastCompleted,
    /** A completed run that did not reach everything — worth saying so rather than implying it did. */
    lastCompletedWasPartial: lastCompleted ? !lastCompleted.clean : false,
    lastFailure: runs.find(run => run.status === 'failed'),
    settings: settingsQuery.data,
    /** The interval to show: the one being saved while a save is in flight, so the choice does not snap back. */
    refreshIntervalHours:
      (saveSettings.isPending ? saveSettings.variables?.refreshIntervalHours : undefined) ??
      settingsQuery.data?.refreshIntervalHours,
    setRefreshInterval: (refreshIntervalHours: number) =>
      saveSettings.mutate({
        refreshIntervalHours,
        autoRefreshEnabled: settingsQuery.data?.autoRefreshEnabled ?? true,
      }),
    setAutoRefresh: (autoRefreshEnabled: boolean) =>
      saveSettings.mutate({
        refreshIntervalHours: settingsQuery.data?.refreshIntervalHours ?? 24,
        autoRefreshEnabled,
      }),
    isSavingSettings: saveSettings.isPending,
    settingsError: saveSettings.error,
    requestSync: () => mutation.mutate(),
    isRequesting: mutation.isPending,
    requestError: mutation.error,
  };
}
